"use client";

import { RecorderPanel } from "@/components/recording/RecorderPanel";
import { ShareModal } from "@/components/share/ShareModal";
import { RecordingLossConfirmModal } from "@/components/recording/RecordingLossConfirmModal";
import { setNavigationConfirmed } from "@/components/recording/RecorderPanel";
import { HeaderNavigation } from "@/components/layout/HeaderNavigation";
import { useIdentity } from "@/lib/identity/useIdentity";
import { getLocalUserId } from "@/lib/identity/client";
import { useAdminStatus } from "@/lib/admin/useAdminStatus";
import { type EncodedAudio } from "@/lib/audio/encoding";
import { RefreshCw, LogOut } from "lucide-react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useState, useCallback, useEffect, useRef } from "react";
import { registerActiveRecordingCheck } from "@/lib/navigation/backButtonWarning";

interface PublishedPostData {
  slug: string;
  url: string;
  pseudonym: string;
  waveform_url?: string | null;
  og_image_url?: string | null;
  og_image_data_url?: string | null; // Client-generated data URL for immediate display
}

export default function Home() {
  const { identity, loading, rerollPseudonym, refreshIdentity } = useIdentity();
  const { isAdmin } = useAdminStatus();
  const router = useRouter();
  const [showShareModal, setShowShareModal] = useState(false);
  const [publishedPost, setPublishedPost] = useState<PublishedPostData | null>(null);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [recordingLossModalOpen, setRecordingLossModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [recorderState, setRecorderState] = useState<"idle" | "recording" | "processing" | "preview" | "uploading">("idle");
  // Ref to track recorder state synchronously (for popstate handler)
  const recorderStateRef = useRef<"idle" | "recording" | "processing" | "preview" | "uploading">("idle");

  /**
   * Convert Blob to base64 string
   */
  const blobToBase64 = async (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        // Remove the data URL prefix (e.g., "data:audio/mpeg;base64,")
        const base64String = base64.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  /**
   * Handle publishing a post
   */
  const handlePublish = async (audio: EncodedAudio) => {
    try {
      
      // Get local user ID
      const localUserId = getLocalUserId();
      if (!localUserId) {
        throw new Error("Failed to get user identity");
      }

      if (!identity?.pseudonym) {
        throw new Error("Failed to get pseudonym");
      }

      // Generate OG image on client for immediate display (optimization)
      let ogImageDataUrl: string | null = null;
      let ogImageBase64: string | null = null;
      let waveformPeaks: number[] | null = null;
      
      try {
        const { generateWaveformPeaks } = await import("@/lib/waveform/og-image");
        const { generateOGWaveformImage } = await import("@/lib/waveform/og-image-client");
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const arrayBuffer = await audio.mp3Blob.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        
        // Generate waveform peaks for server-side fallback
        waveformPeaks = generateWaveformPeaks(audioBuffer, 220);
        
        // Generate a temporary slug for client-side generation (will be replaced by server)
        // Use a simple hash of timestamp + random for uniqueness
        const tempSlug = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        // Generate OG image on client for immediate display (matching server-side design)
        const ogImageBlob = await generateOGWaveformImage({
          audioBuffer,
          pseudonym: identity.pseudonym,
          durationMs: audio.durationMs,
          slug: tempSlug, // Temporary slug for seed generation
        });
        
        // Convert to base64 for server upload
        ogImageBase64 = await blobToBase64(ogImageBlob);
        
        // Convert to data URL for immediate display (no network request needed)
        ogImageDataUrl = `data:image/png;base64,${ogImageBase64}`;
        
        audioContext.close();
      } catch (ogError) {
        console.error("Failed to generate OG image on client:", ogError);
        // Continue without client-generated image - server will generate it
      }

      // Convert audio blob to base64
      const audioBase64 = await blobToBase64(audio.mp3Blob);

      // Call the API
      const response = await fetch("/api/post/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          local_user_id: localUserId,
          audio_base64: audioBase64,
          duration_ms: audio.durationMs,
          waveform_peaks: waveformPeaks,
          og_image_base64: ogImageBase64, // Send client-generated image for server upload
          pseudonym: identity.pseudonym,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || "Failed to publish post";
        
        // Treat 503 (Service Unavailable) as a network/connectivity issue
        if (response.status === 503) {
          throw new Error("Not able to connect. Please check your internet connection and try again.");
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Show share modal instead of navigating directly
      // Use client-generated data URL for immediate display, fallback to CDN URL when available
      setPublishedPost({
        slug: data.slug,
        url: data.url,
        pseudonym: data.pseudonym,
        waveform_url: data.waveform_url,
        og_image_url: data.og_image_url, // CDN URL for social sharing
        og_image_data_url: ogImageDataUrl, // Client-generated data URL for immediate display
      });
      setShowShareModal(true);

      // Refresh identity to update can_reroll status (now locked after first post)
      refreshIdentity();

    } catch (error) {
      console.error("Failed to publish post:", error);
      
      // If error already has the network error message, re-throw it as-is
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes("Not able to connect")) {
        throw error;
      }
      
      // Detect network errors and provide user-friendly message
      const isNetworkError = error instanceof TypeError && (
        (typeof navigator !== 'undefined' && !navigator.onLine) || // Browser reports offline
        (error.message && (
          error.message.includes('fetch failed') ||
          error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError') ||
          error.message.toLowerCase().includes('network')
        ))
      );
      
      if (isNetworkError) {
        throw new Error("Not able to connect. Please check your internet connection and try again.");
      }
      
      // Re-throw other errors as-is so RecorderPanel can catch and display them
      throw error;
    }
  };

  // Close modal only - no navigation (per FR-6.1)
  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  // Handle browser back/forward button navigation (task 7.4.2)
  // Use shared module to avoid conflicts between pages
  useEffect(() => {
    // Register our check function
    const check = () => {
      return recorderStateRef.current === "recording" || 
             recorderStateRef.current === "preview" ||
             recorderStateRef.current === "uploading";
    };
    
    const unregister = registerActiveRecordingCheck(check);
    
    return () => {
      unregister();
    };
  }, []);

  // Back button warning using guard entry approach
  const guardPushedRef = useRef(false);
  const handlingPopstateRef = useRef(false);
  
  // Push guard entry when recording becomes active
  useEffect(() => {
    const isRecording =
      recorderState === "recording" ||
      recorderState === "preview" ||
      recorderState === "uploading";
    
    if (isRecording && !guardPushedRef.current) {
      // Small delay to let recording fully initialize
      const timer = setTimeout(() => {
        if (!guardPushedRef.current && 
            (recorderStateRef.current === "recording" ||
              recorderStateRef.current === "preview" ||
              recorderStateRef.current === "uploading")) {
          history.pushState({ __vocly_guard__: true }, '', window.location.href);
          guardPushedRef.current = true;
        }
      }, 100);
      return () => clearTimeout(timer);
    } else if (!isRecording && guardPushedRef.current) {
      // Recording ended - mark guard as not active (but don't remove from history)
      guardPushedRef.current = false;
    }
  }, [recorderState]);
  
  // Popstate handler
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (handlingPopstateRef.current) return;
      
      const isGuardState = e.state && e.state.__vocly_guard__;
      
      // If navigating TO guard, mark it
      if (isGuardState) {
        guardPushedRef.current = true;
        return;
      }
      
      // Navigated away from guard (or no guard) - check if recording active
      const isRecording =
        recorderStateRef.current === "recording" ||
        recorderStateRef.current === "preview" ||
        recorderStateRef.current === "uploading";
      
      if (!isRecording) {
        guardPushedRef.current = false;
        return;
      }
      
      // Recording active, user pressed back
      handlingPopstateRef.current = true;
      
      const isPreview = recorderStateRef.current === "preview";
      const message = isPreview
        ? "You have a recording ready to post.\nLeaving now will discard it."
        : "You have a recording in progress.\nLeaving now will discard it.";
      
      const confirmed = window.confirm(message);
      
      if (!confirmed) {
        // Push new guard to stay on page
        history.pushState({ __vocly_guard__: true }, '', window.location.href);
        guardPushedRef.current = true;
      } else {
        // User confirmed - if we were on a guard, need to go back again
        if (guardPushedRef.current) {
          guardPushedRef.current = false;
          history.back();
        }
      }
      
      handlingPopstateRef.current = false;
    };
    
    window.addEventListener('popstate', handlePopState, true);
    return () => window.removeEventListener('popstate', handlePopState, true);
  }, []);


  // Navigate to post and close modal (for "View Your Post" button)
  const handleViewPost = () => {
    if (publishedPost) {
      router.push(`/s/${publishedPost.slug}`);
    }
    setShowShareModal(false);
  };

  return (
    <div className="flex min-h-screen justify-center px-4 py-8 md:px-4 md:py-8">
      {/* 760px Shell */}
      <div className="w-full max-w-[760px]">
        {/* Header */}
        <header className="mb-8 flex items-end justify-between">
          <div className="flex items-center gap-1">
            <Link href="/" className="inline-flex items-end transition-opacity hover:opacity-80">
              <img
                src="/logo-vocly-mvp.svg"
                alt="Vocly Logo"
                width={56}
                height={56}
                fetchPriority="high"
                loading="eager"
                className="h-[56px] w-auto"
                style={{ 
                  display: 'block', 
                  marginLeft: '-14px',
                  transform: 'translateZ(0)',
                  backfaceVisibility: 'hidden'
                }}
              />
              <div className="text-2xl uppercase tracking-wide text-white" style={{ letterSpacing: '0.05em', fontWeight: 850, marginLeft: '-14px' }}>
                VOCLY
              </div>
            </Link>
            <span className="text-[10px] text-[#707090] rounded-full border border-[#262636]/40 bg-[rgba(10,10,20,0.2)] px-1.5 py-0.5 font-medium opacity-85">
              Beta
            </span>
          </div>
          <div className="flex items-center gap-4">
            <HeaderNavigation />
          </div>
        </header>

        {/* Main Content */}
        <main className="flex flex-col gap-4">
          {/* Hero Section */}
          <section className="flex flex-col gap-4">
            <h1 className="text-[24px] sm:text-[28px] md:text-[32px] font-[750] leading-[1.15] mb-3">
              <span className="block">
                Post your{" "}
              <span className="bg-gradient-to-r from-[#ffb5e5] to-[#ff3ea5] bg-clip-text text-transparent">
                  voice
                </span>
                {" "}in seconds.
              </span>
              <span className="block mt-3 sm:mt-4 text-white/70">
                <span className="sm:hidden text-xl">
                  Record → preview → share
                </span>
                <span className="hidden sm:block text-xl">Record → preview → share</span>
              </span>
            </h1>
            <div className="mt-3 mb-8 flex flex-wrap gap-2">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-[#262636] bg-[rgba(10,10,20,0.8)] px-2.5 py-1.5 text-[11px] text-[#a3a3c2]">
                <div className="h-1.5 w-1.5 rounded-full bg-gradient-to-br from-[#ffe4f5] via-[#ff3ea5] to-[#b31973] shadow-[0_0_12px_rgba(255,62,165,0.9)]" />
                Zero setup
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-[#2d2d3d] bg-[rgba(10,10,20,0.8)] px-2.5 py-1.5 text-[11px] text-[#a3a3c2]">
                <div className="h-1.5 w-1.5 rounded-full bg-gradient-to-br from-[#ffe4f5] via-[#ff3ea5] to-[#b31973] shadow-[0_0_8px_rgba(255,62,165,0.7)]" />
                One-tap posting
              </div>
            </div>
          </section>

          {/* Hero Columns */}
          <section className="flex flex-col gap-3 -mt-8 sm:-mt-8">
            {/* Record Card with Posting As inside */}
            <div className="rounded-[18px] border border-[rgba(255,255,255,0.04)] bg-gradient-to-br from-[#171727] via-[#111118] to-[#171727] px-4 pb-3.5 pt-4 shadow-[0_18px_45px_rgba(0,0,0,0.6)]">
              {/* When admin is logged in, show message instead of recorder */}
              {isAdmin ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-amber-500/30 bg-amber-500/10">
                    <LogOut className="h-6 w-6 text-amber-400" />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-[#f7f7ff]">Admin Mode Active</h3>
                  <p className="mb-4 max-w-xs text-sm text-[#a3a3c2]">
                    You&apos;re logged in as an admin. Log out from the admin dashboard to create posts as a regular user.
                  </p>
                  <Link
                    href="/admin"
                    className="inline-flex items-center gap-2 rounded-full border border-[#262636] bg-[#0a0a12] px-4 py-2 text-sm text-[#f7f7ff] transition hover:border-[rgba(255,255,255,0.16)] hover:bg-[#141422]"
                  >
                    Go to Admin Dashboard
                  </Link>
                </div>
              ) : (
                <>
                  {rateLimitError && (
                    <div className="mb-4 rounded-2xl border border-rose-400/20 bg-rose-500/5 p-3 text-sm text-rose-200/80">
                      {rateLimitError}
                      <button
                        onClick={() => setRateLimitError(null)}
                        className="ml-2 underline"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}
                  <RecorderPanel 
                    onPublish={handlePublish}
                    onBeforeStart={async () => {
                      // Check thread-join limit before starting recording on homepage
                      try {
                        const localUserId = getLocalUserId();
                        if (!localUserId) {
                          // If we can't get local user ID, allow recording (fail open)
                          return true;
                        }
                        const response = await fetch(`/api/rate-limit/check?checkType=new_post&local_user_id=${encodeURIComponent(localUserId)}`);
                        if (!response.ok) {
                          // If check fails, still allow recording (fail open)
                          console.error("Rate limit check failed:", response.statusText);
                          return true;
                        }

                        const data = await response.json();
                        if (!data.allowed) {
                          // Rate limited - show error and don't start recording
                          setRateLimitError(data.error || "You've started too many new conversations recently.");
                          return false;
                        }

                        // Rate limit check passed
                        return true;
                      } catch (error) {
                        // If check fails, still allow recording (fail open)
                        console.error("Rate limit check error:", error);
                        return true;
                      }
                    }}
                    onRateLimitError={(error) => setRateLimitError(error)}
                    onStateChange={(state) => {
                      setRecorderState(state);
                      recorderStateRef.current = state; // Keep ref in sync for synchronous access
                    }}
                    onNavigationAttempt={(href) => {
                      // Check if there's an active recording or preview
                      if (
                        recorderState === "recording" ||
                        recorderState === "preview" ||
                        recorderState === "uploading"
                      ) {
                        setPendingNavigation(href);
                        setRecordingLossModalOpen(true);
                      } else {
                        // No active recording - allow navigation
                        window.location.href = href;
                      }
                    }}
                  />
                  
                  {/* Inline Identity Line - Inside recording pane (only when idle/preview) */}
                  {(recorderState === "idle" || recorderState === "preview") && (
                    <div
                      className={`mt-3 flex items-center justify-center gap-1 text-xs text-slate-400 ${
                        identity && !identity.can_reroll ? "opacity-50" : "opacity-65"
                      }`}
                    >
                      <span>Posting as </span>
                      {loading ? (
                        <span className="font-medium text-slate-100">Generating...</span>
                      ) : identity ? (
                        <>
                          <span
                            className={`font-medium ${
                              identity.can_reroll ? "text-slate-100" : "text-slate-300"
                            }`}
                          >
                            {identity.pseudonym}
                          </span>
                          {identity.can_reroll && (
                            <>
                              <span> · </span>
                              <button
                                onClick={() => void rerollPseudonym()}
                                className="text-xs text-pink-400 hover:text-pink-300 underline-offset-2 hover:underline cursor-pointer"
                              >
                                Change
                              </button>
                            </>
                          )}
                        </>
                      ) : (
                        <span className="font-medium text-red-400">Failed to load</span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </main>

        {/* Microfooter */}
        <footer className="mt-16 mb-4 text-center">
          <p className="text-xs text-white/45 leading-relaxed">
            Built securely with no login required.
            <br className="sm:hidden" />
            <span className="hidden sm:inline"> </span>
            Audio stays on your device until you publish.
          </p>
        </footer>
      </div>

      {/* Share Modal */}
      {publishedPost && (
        <ShareModal
          isOpen={showShareModal}
          onClose={handleCloseShareModal}
          onViewPost={handleViewPost}
          postUrl={publishedPost.url}
          pseudonym={publishedPost.pseudonym}
          waveformUrl={publishedPost.og_image_url || publishedPost.waveform_url}
          ogImageDataUrl={publishedPost.og_image_data_url}
        />
      )}

      {/* Recording Loss Confirmation Modal */}
      <RecordingLossConfirmModal
        isOpen={recordingLossModalOpen}
        onClose={() => {
          setRecordingLossModalOpen(false);
          setPendingNavigation(null);
          // Reset navigation confirmed flag if user cancels
          setNavigationConfirmed(false);
        }}
        onConfirm={() => {
          if (pendingNavigation) {
            // User confirmed navigation - disable beforeunload warning and proceed
            setNavigationConfirmed(true);
            window.location.href = pendingNavigation;
          }
        }}
        title="Leave this page?"
        description={
          recorderState === "preview"
            ? "You have a recording ready to post.\nLeaving now will discard it."
            : "You have a recording in progress.\nLeaving now will discard it."
        }
        confirmButtonText="Discard"
        cancelButtonText="Cancel"
      />
    </div>
  );
}
